import dayjs from 'dayjs'
import store from '@/store'
import { setOrderStyle } from '@/utils/orderMsg-types'
import { setCommodityCardStyle } from '@/utils/commodityCardMsg-types'
import { setCustomerStyle } from '@/utils/customerMsg-types'

import router from '@/router'
/**
 * @url
 * @description 前端可用的消息code，字符串类型为纯展示消息
 */
export const msgTypes = {
  common: 101,
  autoReply: 350
}

export const msgShowTypes = {
  common: 'common',
  tips: 'tips',
  info: 'info',
  warning: 'warning',
  recall: 'recall', // 撤回消息类型
  nope: 'nope', // 不显示此消息
  contactService: 'contactService', // 转接人工客服类型
  selfService: 'selfService', // 自助查询
  evaluation: 'evaluation', // 邀评
  collection: 'collection', // 信息收集
  reconnection: 'reconnection' // 会话转接
}

// 消息展示类型
const msgShowMap = {
  // 客服转移会话提醒
  202: { type: msgShowTypes.tips },
  // 特殊欢迎语
  203: { type: msgShowTypes.tips, historyHidden: true },
  // 客服表单收集，信息收集
  207: { type: msgShowTypes.collection },
  // 评价成功提示语
  215: { type: msgShowTypes.contactService, historyHidden: true },
  // 排队中提示
  301: { type: msgShowTypes.warning, historyHidden: true, temporary: true },
  // 操作成功
  304: { type: msgShowTypes.nope },
  // 接入客服
  308: { type: msgShowTypes.nope },
  // 订单消息
  310: { historyHidden: true },
  // 页面id不匹配
  320: { type: msgShowTypes.warning, historyHidden: true },
  // 排队转留言
  330: { type: msgShowTypes.warning, historyHidden: true, temporary: true },
  // 排队超时提示
  333: { type: msgShowTypes.warning, historyHidden: true, temporary: true },
  // 客户绑定
  334: { type: msgShowTypes.nope, historyHidden: true },
  // 用户主动退出排队
  335: { type: msgShowTypes.nope, historyHidden: true },
  // 分配 CA 客服失败消息
  339: { type: msgShowTypes.info },
  // 接线方案转接文案
  342: { type: msgShowTypes.tips, historyHidden: true },
  // 转人工引导提示语
  352: { type: msgShowTypes.contactService, historyHidden: true },
  // 自助查询
  353: { type: msgShowTypes.selfService },
  // 客服超时没有回复转接提醒
  365: { type: msgShowTypes.reconnection, historyHidden: true },
  // 客服回话回复超时转接成功
  366: { type: msgShowTypes.contactService, historyHidden: false },
  // 客服转接超时处理
  368: { type: msgShowTypes.contactService, historyHidden: true },
  // 新会话
  403: { type: msgShowTypes.nope, historyHidden: true },
  // 对话转移请求
  408: { type: msgShowTypes.nope },
  // 转接结束会话
  410: { type: msgShowTypes.nope, historyHidden: true },
  // 接受方拒绝转移请求
  411: { type: msgShowTypes.nope, historyHidden: true },
  // 抢接结束会话
  413: { type: msgShowTypes.nope, historyHidden: true },
  // 对话转移成功后的话术
  415: { type: msgShowTypes.nope, historyHidden: true },
  // 客服转入待跟进结束会话
  416: { type: msgShowTypes.nope, historyHidden: true },
  // 访客即将超时
  418: { type: msgShowTypes.warning, historyHidden: true },
  // 主动发起会话或者留言等发起的会话id变更类型
  421: { type: msgShowTypes.nope, historyHidden: true },
  // 访客结束会话
  501: { type: msgShowTypes.nope, historyHidden: true },
  // 访客超时结束会话
  502: { type: msgShowTypes.nope, historyHidden: true },
  // 客服结束会话
  601: { type: msgShowTypes.nope, historyHidden: true },
  // 客服超时关闭
  602: { type: msgShowTypes.nope, historyHidden: true },
  // 兜底关闭
  603: { type: msgShowTypes.nope, historyHidden: true },
  // 消息弹框提示
  702: { type: msgShowTypes.nope, historyHidden: true },
  // 不展示的系统消息
  703: { type: msgShowTypes.nope, historyHidden: true },
  // 是否已读
  900: { type: msgShowTypes.nope },
  // 撤回消息通知
  901: { type: msgShowTypes.recall },
  // 撤回重新编辑消息通知
  902: { type: msgShowTypes.recall },
  // 关闭会话邀评
  908: { type: msgShowTypes.evaluation },
  // 客服点击邀评按钮
  909: { type: msgShowTypes.evaluation },
  // 用户点击“评价客服”按钮-普通邀评
  1001: { type: msgShowTypes.evaluation }
}

/**
 * 生成消息体默认数据格式
 */
export const defaultMsgBody = data => {
  return {
    type: msgTypes.common,
    showType: msgShowTypes.common,
    temporary: false,
    msg: '',
    originalContent: '',
    msgId: null,
    messageId: null,
    dialogid: null,
    msgStatus: 0,
    contentType: 0,
    recallTime: null, // 消息被撤回的时间戳
    date: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    isSelf: true,
    name: '我',
    avatar: store.state.chat.userAvatar,
    ...data
  }
}

/**
 * 格式化消息数据
 * @param {Array} msgs 原始消息数据数组
 * @param {boolean} isHistory 是否为历史消息
 * @returns {Array}
 * 104 问答消息类型
 * 106/370 订单消息类型
 * 107 商品卡片类型
 * 108 灯塔客户信息类型
 */
export function formartMsgs(msgs, isHistory = false) {
  // 格式化消息数据
  return msgs.map(msg => {
    const msgType = Number(msg.type)
    const isSelf =
      msg.msgFrom === 1 ||
      msgType === 101 ||
      msgType === 104 ||
      msgType === 106 ||
      msgType === 107 ||
      msgType === 108 ||
      msgType === 310
    let showType = msgShowTypes.common
    let temporary = false
    if (msgShowMap[msgType]) {
      if (isHistory && msgShowMap[msgType].historyHidden) {
        showType = msgShowTypes.nope
      } else {
        showType = msgShowMap[msgType].type
      }
      temporary = msgShowMap[msgType].temporary
    }

    return {
      type: msgType,
      showType,
      temporary,
      msg: msg.content,
      originalContent: msg.originalContent,
      msgId: msg.msgId,
      messageId: msg.messageId,
      dialogid: msg.dialogid, // 对话id
      msgStatus: msg.msgStatus, // -1代表此条消息被撤回 1代表消息被已读
      contentType: msg.contentType, // 内容类型 0文本 1图片 2视频 3文件
      recallTime: msg.recallTime, // 消息被撤回的时间戳
      date: dayjs(msg.createTime).format('YYYY-MM-DD HH:mm:ss'),
      isSelf,
      name: isSelf ? '我' : msg.kefuName || store.state.theme.title,
      avatar: headPortraitDispose(msg, isSelf),
      // 是否为ca客服
      isCA: store.state.chat.chatInfo.CAAgentId > 0 && store.state.chat.chatInfo.CAAgentId == msg.kefuid && !isSelf,
      messageTag: msg.messageTag, // 评价对象
      msgExt: msg.msgExt,
      questionType: msg.questionType,
      review: msg.review,
      component: msg.component
    }
  })
}

/**
 * 格式化消息数据
 * 106 订单消息类型
 * 107 商品卡片消息类型
 */
export function specialMsgs(msgList) {
  if (!Array.isArray(msgList) || msgList.length === 0) {
    return msgList
  }

  // 未来可以添加 108、109 等类型
  const typeHandlerMap = {
    106: setOrderStyle,
    107: setCommodityCardStyle,
    108: setCustomerStyle,
    370: setOrderStyle
  }

  for (const msg of msgList) {
    if ((msg.originalContent || msg.content) && typeHandlerMap[msg.type]) {
      try {
        const sourceContent = msg.originalContent || msg.content
        const orderObj = JSON.parse(sourceContent)
        if (orderObj && typeof orderObj === 'object' && Object.keys(orderObj).length > 0) {
          const content = typeHandlerMap[msg.type](orderObj)
          if (content) {
            msg.content = content
          }
        }
      } catch (e) {
        console.error('JSON:', msg.originalContent || msg.content, e)
      }
    }
    // markdown多个订单消息
    if ((msg.originalContent || msg.content) && msg.type === 220) {
      msg.component = 'order_card_view'
    }
  }

  return msgList
}
/**
 * @description: 头像处理
 * @param {*} msg 当前消息参数
 * @param {*} isSelf 当前消息类型
 * @return {*}
 */
function headPortraitDispose(msg, isSelf) {
  if (!isSelf && !msg.avatar) {
    const { platformId } = router.history.current.query
    const image = store.getters.getPlatformImage(platformId)
    if (image) {
      return image
    }
  }
  return isSelf ? msg.customHeader || store.state.chat.userAvatar : msg.avatar || store.state.chat.agentAvatar
}
